import * as React from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signUp: (email: string, password: string, name?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any; requiresMFA?: boolean }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  deleteAccount: (password: string, mfaCode?: string) => Promise<{ error: any }>;
  // MFA functions
  enrollMFA: () => Promise<{ error: any; qrCode?: string; secret?: string }>;
  verifyMFAEnrollment: (code: string) => Promise<{ error: any }>;
  unenrollMFA: (factorId: string) => Promise<{ error: any }>;
  verifyMFAChallenge: (factorId: string, code: string) => Promise<{ error: any }>;
  getMFAFactors: () => Promise<{ error: any; factors?: any[] }>;
  // Passkey functions
  enrollPasskey: (friendlyName: string) => Promise<{ error: any }>;
  signInWithPasskey: () => Promise<{ error: any }>;
  listPasskeys: () => Promise<{ error: any; passkeys?: any[] }>;
  deletePasskey: (credentialId: string) => Promise<{ error: any }>;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = React.useState<User | null>(null);
  const [session, setSession] = React.useState<Session | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        logger.info('Auth state change:', event, session ? 'session exists' : 'no session');
        
        // If user was previously logged in but now session is null (logout/expiry)
        if (event === 'SIGNED_OUT' || (user && !session)) {
          // Only redirect if we're on a protected route (not already on auth pages)
          const currentPath = window.location.pathname;
          const isProtectedRoute = currentPath.startsWith('/dashboard') || 
                                  currentPath.startsWith('/apps') ||
                                  currentPath.startsWith('/organization') ||
                                  currentPath.startsWith('/queues') ||
                                  currentPath.startsWith('/documents');
          
          if (isProtectedRoute && currentPath !== '/auth') {
            setTimeout(() => {
              window.location.replace('/auth');
            }, 100);
          }
        }
        
        // Check onboarding status when user signs in
        if (session?.user && event === 'SIGNED_IN') {
          setTimeout(async () => {
            const { data: profile } = await supabase
              .from('profiles')
              .select('onboarding_completed')
              .eq('user_id', session.user.id)
              .single();
            
            const currentPath = window.location.pathname;
            if (profile && !profile.onboarding_completed && currentPath !== '/onboarding') {
              window.location.replace('/onboarding');
            }
          }, 0);
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
      }
    );

    // Check for existing session with error handling
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          logger.error('Session check error:', error);
          // Clear invalid session
          setSession(null);
          setUser(null);
          setIsLoading(false);
          return;
        }
        
        // Check onboarding status for existing session
        if (session?.user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('onboarding_completed')
            .eq('user_id', session.user.id)
            .single();
          
          const currentPath = window.location.pathname;
          if (profile && !profile.onboarding_completed && 
              currentPath !== '/onboarding' && currentPath !== '/auth') {
            setSession(session);
            setUser(session.user);
            setIsLoading(false);
            window.location.replace('/onboarding');
            return;
          }
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
      } catch (error) {
        logger.error('Failed to get session:', error);
        setSession(null);
        setUser(null);
        setIsLoading(false);
      }
    };

    checkSession();

    return () => subscription.unsubscribe();
  }, []);


  const signUp = async (email: string, password: string, name?: string) => {
    const redirectUrl = `${window.location.origin}/signupsuccess`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: name ? {
          display_name: name,
          full_name: name,
          email: email // Ensure email is in metadata for Intercom
        } : {
          email: email
        }
      }
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    // Check if MFA is required
    if (error?.message?.includes('MFA') || error?.message?.includes('factor')) {
      return { error, requiresMFA: true };
    }
    
    return { error, requiresMFA: false };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`
      }
    });
    return { error };
  };

  const signOut = async () => {
    try {
      logger.info('Signing out...');
      
      // First, clear local state immediately to prevent stuck states
      setSession(null);
      setUser(null);
      setIsLoading(false);
      
      // Then attempt to sign out from Supabase
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) {
        logger.error('Sign out error:', error);
      }
      
      // Clear any stored session data
      localStorage.removeItem('supabase.auth.token');
      sessionStorage.clear();
      
      // Force a page refresh to clear any cached auth state
      window.location.replace('/auth');
    } catch (error) {
      logger.error('Failed to sign out:', error);
      // Even if sign out fails, ensure clean state
      setSession(null);
      setUser(null);
      setIsLoading(false);
      localStorage.removeItem('supabase.auth.token');
      sessionStorage.clear();
      window.location.replace('/auth');
    }
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  // MFA functions
  const enrollMFA = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
      });
      
      if (error) return { error };
      
      return {
        error: null,
        qrCode: data.totp.qr_code,
        secret: data.totp.secret,
      };
    } catch (error: any) {
      logger.error('MFA enrollment error:', error);
      return { error };
    }
  };

  const verifyMFAEnrollment = async (code: string) => {
    try {
      const factors = await supabase.auth.mfa.listFactors();
      if (factors.error) return { error: factors.error };
      
      const totpFactor = factors.data.totp[0];
      if (!totpFactor) return { error: new Error('No TOTP factor found') };
      
      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId: totpFactor.id,
        code,
      });
      
      return { error };
    } catch (error: any) {
      logger.error('MFA verification error:', error);
      return { error };
    }
  };

  const unenrollMFA = async (factorId: string) => {
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      return { error };
    } catch (error: any) {
      logger.error('MFA unenrollment error:', error);
      return { error };
    }
  };

  const verifyMFAChallenge = async (factorId: string, code: string) => {
    try {
      // First create a challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });
      
      if (challengeError) return { error: challengeError };
      
      // Then verify with the code
      const { error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code,
      });
      return { error };
    } catch (error: any) {
      logger.error('MFA challenge verification error:', error);
      return { error };
    }
  };

  const getMFAFactors = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) return { error };
      
      return { error: null, factors: data.totp };
    } catch (error: any) {
      logger.error('Get MFA factors error:', error);
      return { error };
    }
  };

  // Passkey functions - Currently not supported by Supabase, placeholder for future
  const enrollPasskey = async (friendlyName: string) => {
    try {
      // Passkeys via WebAuthn are not yet supported in Supabase Auth
      // This is a placeholder for when the feature becomes available
      return { error: new Error('Passkey enrollment is not yet supported. Coming soon!') };
    } catch (error: any) {
      logger.error('Passkey enrollment error:', error);
      return { error };
    }
  };

  const signInWithPasskey = async () => {
    try {
      return { error: new Error('Passkey sign-in is not yet supported. Coming soon!') };
    } catch (error: any) {
      logger.error('Passkey sign-in error:', error);
      return { error };
    }
  };

  const listPasskeys = async () => {
    try {
      // Return empty array since passkeys are not yet supported
      return { error: null, passkeys: [] };
    } catch (error: any) {
      logger.error('List passkeys error:', error);
      return { error };
    }
  };

  const deletePasskey = async (credentialId: string) => {
    try {
      return { error: new Error('Passkey deletion is not yet supported. Coming soon!') };
    } catch (error: any) {
      logger.error('Delete passkey error:', error);
      return { error };
    }
  };

  const deleteAccount = async (password: string, mfaCode?: string) => {
    try {
      if (!user?.email) {
        return { error: new Error('No user email found') };
      }

      // Step 1: Re-authenticate with password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password,
      });

      if (signInError) {
        logger.error('Password verification failed:', signInError);
        return { error: new Error('Invalid password') };
      }

      // Step 2: Check if MFA is enabled and verify if provided
      const { factors } = await getMFAFactors();
      if (factors && factors.length > 0) {
        if (!mfaCode) {
          return { error: new Error('MFA code required') };
        }

        // Verify MFA code
        const { error: mfaError } = await verifyMFAChallenge(factors[0].id, mfaCode);
        if (mfaError) {
          logger.error('MFA verification failed:', mfaError);
          return { error: new Error('Invalid MFA code') };
        }
      }

      // Step 3: Call edge function to delete all user data
      const { error: deleteError } = await supabase.functions.invoke('queue-assist-delete-user', {
        body: { userId: user.id }
      });

      if (deleteError) {
        logger.error('Account deletion failed:', deleteError);
        return { error: deleteError };
      }

      // Step 4: Sign out
      await signOut();
      
      return { error: null };
    } catch (error: any) {
      logger.error('Delete account error:', error);
      return { error };
    }
  };

  const value = {
    user,
    session,
    isLoading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    deleteAccount,
    enrollMFA,
    verifyMFAEnrollment,
    unenrollMFA,
    verifyMFAChallenge,
    getMFAFactors,
    enrollPasskey,
    signInWithPasskey,
    listPasskeys,
    deletePasskey,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};